import React, { useEffect, useState } from 'react';
import { getCustomers, createCustomer, updateCustomer, addCustomerNote } from '../services/api';
import api from '../services/api';
import toast from 'react-hot-toast';

const DELIVERY_OPTIONS = [['VET','VET'],['JT_COD','J&T (COD)'],['JT_NON_COD','J&T (Non COD)'],['DOM','Delivery (DOM)'],['SONA','Delivery (Sona)'],['TAKE_SHOP','Take Shop'],['GRAB','Grab']];
const DELIVERY_LABELS = Object.fromEntries(DELIVERY_OPTIONS.map(([v,l]) => [v,l]));

function useDeliveryMethods() {
  const [methods, setMethods] = useState([]);
  useEffect(() => {
    api.get('/customers/delivery-methods').then(r => setMethods(r.data.filter(m => m.is_active))).catch(()=>{});
  }, []);
  return methods;
}

const RANK_BORDER = { new: '#4ade80', normal: '#93c5fd', silver: '#cbd5e1', gold: '#fbbf24', platinum: '#1f2937', blacklist: '#f87171' };

function cleanPhone(raw) {
  if (!raw) return '';
  const khmer = '០១២៣៤៥៦៧៨៩';
  let s = raw.split('').map(c => { const i = khmer.indexOf(c); return i >= 0 ? String(i) : c; }).join('');
  return s.replace(/[^\d+]/g, '');
}

function copyName(name) {
  navigator.clipboard.writeText(name || '').then(() => toast.success('Copied: ' + name)).catch(() => {});
}

function getBorderColor(c) {
  if (c.is_blacklisted) return RANK_BORDER.blacklist;
  if (c.ranking && c.ranking !== 'normal') return RANK_BORDER[c.ranking] || RANK_BORDER.normal;
  if (c.is_new) return RANK_BORDER.new;
  return RANK_BORDER.normal;
}

function Avatar({ c, size = 36 }) {
  const border = getBorderColor(c);
  const style = { width: size, height: size, borderRadius: '50%', border: `2.5px solid ${border}`, flexShrink: 0, objectFit: 'cover', cursor: 'pointer' };
  const el = c.fb_profile_pic
    ? <img src={c.fb_profile_pic} alt="" style={style} />
    : <div style={{ ...style, background: 'linear-gradient(135deg,#7c6af5,#c084fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.38, fontWeight: 700, color: '#fff' }}>{(c.name||'?')[0].toUpperCase()}</div>;
  return <div onClick={e => { e.stopPropagation(); copyName(c.name); }} style={{ flexShrink: 0, cursor: 'pointer' }} title="Click to copy name">{el}</div>;
}

function PhoneEditor({ phones, onChange }) {
  const add = () => onChange([...phones, '']);
  const remove = (i) => onChange(phones.filter((_, j) => j !== i));
  const update = (i, val) => { const p = [...phones]; p[i] = cleanPhone(val); onChange(p); };
  return (
    <div>
      {phones.map((p, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
          <input value={p} onChange={e => update(i, e.target.value)} placeholder={i === 0 ? 'Primary phone' : `Phone ${i+1}`}
            style={{ flex: 1, background: '#f8f9fa', border: '1.5px solid #dee2e6', borderRadius: 8, padding: '9px 13px', fontSize: 14, outline: 'none' }} />
          {phones.length > 1 && <button type="button" onClick={() => remove(i)} style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 6, width: 32, height: 32, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>}
        </div>
      ))}
      <button type="button" onClick={add} style={{ background: '#f0f9ff', color: '#0369a1', border: '1px dashed #bae6fd', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>+ Add Phone</button>
    </div>
  );
}

function DeliveryMethodsModal({ onClose }) {
  const [methods, setMethods] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [addForm, setAddForm] = useState({ code: '', name: '', fee: '' });
  const [adding, setAdding] = useState(false);
  const inp = { background: '#f8f9fa', border: '1.5px solid #dee2e6', borderRadius: 8, padding: '7px 11px', fontSize: 13, outline: 'none' };
  const load = async () => { try { const r = await api.get('/customers/delivery-methods'); setMethods(r.data); } catch {} };
  useEffect(() => { load(); }, []);
  const save = async id => { try { await api.put(`/customers/delivery-methods/${id}`, editForm); toast.success('Updated'); setEditId(null); load(); } catch { toast.error('Failed'); } };
  const add = async () => { try { await api.post('/customers/delivery-methods', addForm); toast.success('Added'); setAddForm({ code:'', name:'', fee:'' }); setAdding(false); load(); } catch(e) { toast.error(e.response?.data?.detail||'Failed'); } };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:300 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:520, maxHeight:'85vh', overflowY:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20 }}>
          <div style={{ fontWeight:700, fontSize:17 }}>🚚 Delivery Methods</div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#6c757d' }}>✕</button>
        </div>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13, marginBottom:16 }}>
          <thead><tr style={{ background:'#f9fafb', borderBottom:'1px solid #e5e7eb' }}>
            {['Code','Name','Fee ($)','Active',''].map(h=><th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:600, color:'#374151', fontSize:12 }}>{h}</th>)}
          </tr></thead>
          <tbody>{methods.map(m=>(
            <tr key={m.id} style={{ borderBottom:'1px solid #f3f4f6' }}>
              <td style={{ padding:'8px 12px', fontFamily:'monospace', fontWeight:700, color:'#0369a1' }}>{m.code}</td>
              <td style={{ padding:'8px 12px' }}>{editId===m.id?<input value={editForm.name} onChange={e=>setEditForm(f=>({...f,name:e.target.value}))} style={{...inp,width:130}}/>:m.name}</td>
              <td style={{ padding:'8px 12px' }}>{editId===m.id?<input type="number" step="0.5" value={editForm.fee} onChange={e=>setEditForm(f=>({...f,fee:e.target.value}))} style={{...inp,width:70}}/>:`$${parseFloat(m.fee).toFixed(2)}`}</td>
              <td style={{ padding:'8px 12px' }}>{editId===m.id?<select value={editForm.is_active} onChange={e=>setEditForm(f=>({...f,is_active:parseInt(e.target.value)}))} style={inp}><option value={1}>Yes</option><option value={0}>No</option></select>:<span style={{color:m.is_active?'#16a34a':'#dc2626',fontWeight:600}}>{m.is_active?'Yes':'No'}</span>}</td>
              <td style={{ padding:'8px 12px' }}>{editId===m.id
                ?<div style={{display:'flex',gap:6}}><button onClick={()=>save(m.id)} style={{background:'#7c6af5',color:'#fff',border:'none',borderRadius:6,padding:'4px 10px',fontSize:12,cursor:'pointer'}}>Save</button><button onClick={()=>setEditId(null)} style={{background:'#dee2e6',color:'#495057',border:'none',borderRadius:6,padding:'4px 10px',fontSize:12,cursor:'pointer'}}>✕</button></div>
                :<button onClick={()=>{setEditId(m.id);setEditForm({name:m.name,fee:m.fee,is_active:m.is_active});}} style={{background:'#f3f4f6',color:'#374151',border:'none',borderRadius:6,padding:'4px 10px',fontSize:12,cursor:'pointer'}}>✏️</button>}
              </td>
            </tr>
          ))}</tbody>
        </table>
        {adding
          ?<div style={{display:'flex',gap:8,alignItems:'center',padding:10,background:'#f9fafb',borderRadius:8}}>
            <input placeholder="CODE" value={addForm.code} onChange={e=>setAddForm(f=>({...f,code:e.target.value.toUpperCase()}))} style={{...inp,width:80}}/>
            <input placeholder="Name" value={addForm.name} onChange={e=>setAddForm(f=>({...f,name:e.target.value}))} style={{...inp,flex:1}}/>
            <input type="number" placeholder="Fee" step="0.5" value={addForm.fee} onChange={e=>setAddForm(f=>({...f,fee:e.target.value}))} style={{...inp,width:70}}/>
            <button onClick={add} style={{background:'#7c6af5',color:'#fff',border:'none',borderRadius:6,padding:'7px 14px',fontSize:13,cursor:'pointer'}}>Add</button>
            <button onClick={()=>setAdding(false)} style={{background:'#dee2e6',color:'#495057',border:'none',borderRadius:6,padding:'7px 10px',fontSize:13,cursor:'pointer'}}>✕</button>
          </div>
          :<button onClick={()=>setAdding(true)} style={{background:'#f0f9ff',color:'#0369a1',border:'1px dashed #bae6fd',borderRadius:8,padding:'8px 16px',fontSize:13,fontWeight:600,cursor:'pointer',width:'100%'}}>+ Add Method</button>
        }
      </div>
    </div>
  );
}

function RankingsModal({ onClose }) {
  const [rankings, setRankings] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const inp = { background:'#f8f9fa', border:'1.5px solid #dee2e6', borderRadius:8, padding:'7px 11px', fontSize:13, outline:'none' };
  const load = async () => { try { const r = await api.get('/customers/rankings'); setRankings(r.data); } catch {} };
  useEffect(() => { load(); }, []);
  const save = async id => { try { await api.put(`/customers/rankings/${id}`, editForm); toast.success('Updated'); setEditId(null); load(); } catch { toast.error('Failed'); } };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:300 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:580, maxHeight:'85vh', overflowY:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20 }}>
          <div style={{ fontWeight:700, fontSize:17 }}>⭐ Customer Rankings</div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#6c757d' }}>✕</button>
        </div>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead><tr style={{ background:'#f9fafb', borderBottom:'1px solid #e5e7eb' }}>
            {['','Name','Description','Customers','Active',''].map(h=><th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:600, color:'#374151', fontSize:12 }}>{h}</th>)}
          </tr></thead>
          <tbody>{rankings.map(r=>(
            <tr key={r.id} style={{ borderBottom:'1px solid #f3f4f6' }}>
              <td style={{ padding:'8px 12px' }}><div style={{ width:16, height:16, borderRadius:'50%', background:r.border_color, border:'1px solid #e5e7eb' }} /></td>
              <td style={{ padding:'8px 12px' }}>{editId===r.id?<input value={editForm.name} onChange={e=>setEditForm(f=>({...f,name:e.target.value}))} style={{...inp,width:90}}/>:<span style={{fontWeight:700,color:r.color}}>{r.name}</span>}</td>
              <td style={{ padding:'8px 12px' }}>{editId===r.id?<input value={editForm.description||''} onChange={e=>setEditForm(f=>({...f,description:e.target.value}))} style={{...inp,width:170}}/>:<span style={{color:'#6b7280',fontSize:12}}>{r.description}</span>}</td>
              <td style={{ padding:'8px 12px', textAlign:'center', fontWeight:700 }}>{r.customer_count}</td>
              <td style={{ padding:'8px 12px' }}>{editId===r.id?<select value={editForm.is_active} onChange={e=>setEditForm(f=>({...f,is_active:parseInt(e.target.value)}))} style={inp}><option value={1}>Yes</option><option value={0}>No</option></select>:<span style={{color:r.is_active?'#16a34a':'#dc2626',fontWeight:600}}>{r.is_active?'Yes':'No'}</span>}</td>
              <td style={{ padding:'8px 12px' }}>{editId===r.id
                ?<div style={{display:'flex',gap:6}}><button onClick={()=>save(r.id)} style={{background:'#7c6af5',color:'#fff',border:'none',borderRadius:6,padding:'4px 10px',fontSize:12,cursor:'pointer'}}>Save</button><button onClick={()=>setEditId(null)} style={{background:'#dee2e6',color:'#495057',border:'none',borderRadius:6,padding:'4px 10px',fontSize:12,cursor:'pointer'}}>✕</button></div>
                :<button onClick={()=>{setEditId(r.id);setEditForm({name:r.name,description:r.description||'',is_active:r.is_active});}} style={{background:'#f3f4f6',color:'#374151',border:'none',borderRadius:6,padding:'4px 10px',fontSize:12,cursor:'pointer'}}>✏️</button>}
              </td>
            </tr>
          ))}</tbody>
        </table>
        <div style={{ marginTop:16, padding:12, background:'#f9fafb', borderRadius:8 }}>
          <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:8 }}>Border Color Legend</div>
          <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
            {[['New','#4ade80'],['Normal','#93c5fd'],['Silver','#cbd5e1'],['Gold','#fbbf24'],['Platinum','#1f2937'],['Blacklist','#f87171']].map(([label,color])=>(
              <div key={label} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#374151' }}>
                <div style={{ width:14, height:14, borderRadius:'50%', background:color, border:'1px solid #e5e7eb' }} />{label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


function InlinePhoneEdit({ customer, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [phones, setPhones] = useState([]);
  const inp = { background:'#fff', border:'1px solid #7c6af5', borderRadius:6, padding:'4px 8px', fontSize:12, outline:'none', width:'100%' };

  const start = () => {
    setPhones(customer.phones?.length ? [...customer.phones] : customer.phone ? [customer.phone] : ['']);
    setEditing(true);
  };
  const save = async () => {
    const cleaned = phones.map(cleanPhone).filter(Boolean);
    try {
      await updateCustomer(customer.id, { phones: cleaned, phone: cleaned[0]||'', phone2: cleaned[1]||'' });
      toast.success('Phone updated');
      setEditing(false);
      onSaved();
    } catch { toast.error('Failed'); }
  };

  if (editing) return (
    <div onClick={e=>e.stopPropagation()}>
      {phones.map((p,i) => (
        <div key={i} style={{ display:'flex', gap:4, marginBottom:4 }}>
          <input value={p} onChange={e=>{ const n=[...phones]; n[i]=e.target.value; setPhones(n); }}
            onKeyDown={e=>{ if(e.key==='Enter') save(); if(e.key==='Escape') setEditing(false); }}
            style={inp} autoFocus={i===0} />
          {phones.length > 1 && <button onClick={()=>setPhones(phones.filter((_,j)=>j!==i))} style={{background:'#fee2e2',color:'#dc2626',border:'none',borderRadius:4,padding:'2px 6px',cursor:'pointer',fontSize:12}}>×</button>}
        </div>
      ))}
      <div style={{ display:'flex', gap:4, marginTop:4 }}>
        <button onClick={()=>setPhones([...phones,''])} style={{background:'#f0f9ff',color:'#0369a1',border:'none',borderRadius:4,padding:'2px 8px',fontSize:11,cursor:'pointer'}}>+</button>
        <button onClick={save} style={{background:'#7c6af5',color:'#fff',border:'none',borderRadius:4,padding:'2px 8px',fontSize:11,cursor:'pointer'}}>✓</button>
        <button onClick={()=>setEditing(false)} style={{background:'#f3f4f6',color:'#374151',border:'none',borderRadius:4,padding:'2px 8px',fontSize:11,cursor:'pointer'}}>✕</button>
      </div>
    </div>
  );

  return (
    <div onClick={start} style={{ cursor:'text', minHeight:20 }} title="Click to edit">
      {(customer.phones||[customer.phone,customer.phone2]).filter(Boolean).map((p,i)=>(
        <div key={i} style={{ fontSize:11, color:'#374151' }}>{p}</div>
      ))}
      {!customer.phone && !customer.phones?.length && <span style={{color:'#adb5bd',fontSize:12}}>+ Add phone</span>}
    </div>
  );
}

function InlineLocationEdit({ customer, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [address, setAddress] = useState(customer.address||'');
  const inp = { background:'#fff', border:'1px solid #7c6af5', borderRadius:6, padding:'4px 8px', fontSize:12, outline:'none', width:'100%' };

  const save = async () => {
    try {
      await updateCustomer(customer.id, { address });
      toast.success('Address updated');
      setEditing(false);
      onSaved();
    } catch { toast.error('Failed'); }
  };

  if (editing) return (
    <div onClick={e=>e.stopPropagation()}>
      <input value={address} onChange={e=>setAddress(e.target.value)} autoFocus
        onKeyDown={e=>{ if(e.key==='Enter') save(); if(e.key==='Escape') setEditing(false); }}
        placeholder="Address..." style={{...inp, marginBottom:4}} />
      <div style={{ display:'flex', gap:4 }}>
        <button onClick={save} style={{background:'#7c6af5',color:'#fff',border:'none',borderRadius:4,padding:'2px 8px',fontSize:11,cursor:'pointer'}}>✓ Save</button>
        <button onClick={()=>setEditing(false)} style={{background:'#f3f4f6',color:'#374151',border:'none',borderRadius:4,padding:'2px 8px',fontSize:11,cursor:'pointer'}}>✕</button>
      </div>
    </div>
  );

  return (
    <div onClick={()=>setEditing(true)} style={{ cursor:'text', minHeight:20 }} title="Click to edit">
      {customer.address
        ? <div style={{ fontSize:12, color:'#374151' }}>{customer.address}</div>
        : <span style={{color:'#adb5bd',fontSize:11}}>+ Add address</span>}
    </div>
  );
}

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');

  const [rankingFilter, setRankingFilter] = useState('');
  const [deliveryFilter, setDeliveryFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showRankings, setShowRankings] = useState(false);
  const [showDelivery, setShowDelivery] = useState(false);
  const [note, setNote] = useState('');
  const [stats, setStats] = useState(null);
  const [editCustomer, setEditCustomer] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [newPhones, setNewPhones] = useState(['']);
  const [editPhones, setEditPhones] = useState(['']);

  const deliveryMethods = useDeliveryMethods();
  useEffect(() => { if (selected) loadStats(selected.id); else setStats(null); }, [selected]);

  const blankForm = { name:'', address:'', location_type:null, delivery_preference:null, preferred_delivery:null };

  useEffect(() => { load(); }, [search, rankingFilter, deliveryFilter]);
  useEffect(() => {
    if (editCustomer) {
      setEditForm({ name:editCustomer.name||'', address:editCustomer.address||'', province:editCustomer.province||'', location_type:editCustomer.location_type||'unknown', preferred_delivery:editCustomer.preferred_delivery||'', delivery_preference:editCustomer.delivery_preference||'unknown', notes:editCustomer.notes||'' });
      setEditPhones(editCustomer.phones?.length ? editCustomer.phones : (editCustomer.phone ? [editCustomer.phone] : ['']));
    }
  }, [editCustomer]);

  async function loadStats(id) {
    try { const r = await api.get(`/customers/${id}/stats`); setStats(r.data); }
    catch { setStats(null); }
  }

  async function load() {
    try {
      const params = {};
      if (search) params.search = search;
      if (rankingFilter === 'new') params.is_new = 1;
      else if (rankingFilter) params.ranking = rankingFilter;
      if (deliveryFilter === 'none') params.no_delivery = 1;
      else if (deliveryFilter) params.preferred_delivery = deliveryFilter;
      const r = await getCustomers(params);
      setCustomers(r.data.customers);
      setTotal(r.data.total);
    } catch { toast.error('Failed to load customers'); }
  }

  async function handleCreate(e) {
    e.preventDefault();
    const phones = newPhones.map(cleanPhone).filter(Boolean);
    try {
      await createCustomer({ ...blankForm, ...{ name: e.target.name?.value }, phones, phone: phones[0]||'', phone2: phones[1]||'', address: e.target.address?.value||'', preferred_delivery: e.target.preferred_delivery?.value||null, ranking: e.target.ranking?.value||'normal' });
      toast.success('Customer created');
      setShowForm(false);
      setNewPhones(['']);
      load();
    } catch(e) { toast.error(e.response?.data?.detail || JSON.stringify(e.response?.data) || 'Failed'); console.error(e.response?.data); }
  }

  const inp = { background:'#f8f9fa', border:'1.5px solid #dee2e6', borderRadius:8, padding:'9px 13px', color:'#212529', fontSize:14, width:'100%', outline:'none', boxSizing:'border-box' };
  const lbl = { display:'block', fontSize:11, fontWeight:600, color:'#6c757d', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.04em' };
  const btnSm = (bg, color) => ({ background:bg, color, border:'none', borderRadius:6, padding:'4px 10px', fontSize:11, fontWeight:600, cursor:'pointer' });

  return (
    <div style={{ display:'grid', gridTemplateColumns: selected ? '1fr 380px' : '1fr', gap:20 }}>
      <div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <div>
            <h1 style={{ fontSize:24, fontWeight:700, color:'#212529' }}>Customers</h1>
            <p style={{ color:'#6c757d', fontSize:13 }}>{total} total</p>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={()=>setShowDelivery(true)} style={{ background:'#f0f9ff', color:'#0369a1', border:'1px solid #bae6fd', borderRadius:8, padding:'9px 14px', fontSize:13, fontWeight:600, cursor:'pointer' }}>🚚 Delivery Methods</button>
            <button onClick={()=>setShowRankings(true)} style={{ background:'#faf5ff', color:'#7c3aed', border:'1px solid #ddd6fe', borderRadius:8, padding:'9px 14px', fontSize:13, fontWeight:600, cursor:'pointer' }}>⭐ Rankings</button>
            <button onClick={()=>setShowForm(true)} style={{ background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'9px 18px', fontSize:13, fontWeight:600, cursor:'pointer' }}>+ New Customer</button>
          </div>
        </div>

        <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
          <input style={{ ...inp, maxWidth:240 }} placeholder="Search name, phone, address..." value={search} onChange={e=>setSearch(e.target.value)} />

          <select style={{ ...inp, maxWidth:150 }} value={rankingFilter} onChange={e=>setRankingFilter(e.target.value)}>
            <option value="">All Rankings</option>
            <option value="new">🟢 New</option>
            <option value="normal">⚪ Normal</option>
            <option value="silver">🥈 Silver</option>
            <option value="gold">🥇 Gold</option>
            <option value="platinum">💎 Platinum</option>
            <option value="blacklist">⛔ Blacklisted</option>
          </select>
          <select style={{ ...inp, maxWidth:180 }} value={deliveryFilter} onChange={e=>setDeliveryFilter(e.target.value)}>
            <option value="">All Delivery</option>
            <option value="none">— Not Set</option>
            {deliveryMethods.map(m=><option key={m.code} value={m.code}>{m.name}</option>)}
          </select>
        </div>

        <div style={{ background:'#fff', border:'1px solid #dee2e6', borderRadius:14, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ borderBottom:'1px solid #dee2e6' }}>
                {['Customer','Phone','Address','Preferred Delivery','Orders','Spend'].map(h=>(
                  <th key={h} style={{ padding:'11px 16px', textAlign:'left', fontSize:11, fontWeight:600, color:'#6c757d', textTransform:'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {customers.map(c=>(
                <tr key={c.id} onClick={()=>setSelected(c)} style={{ borderBottom:'1px solid #e9ecef', cursor:'pointer', background: selected?.id===c.id ? '#7c6af510' : 'transparent' }}
                  onMouseEnter={e=>e.currentTarget.style.background='#fafafa'}
                  onMouseLeave={e=>e.currentTarget.style.background= selected?.id===c.id ? '#7c6af510' : 'transparent'}>
                  <td style={{ padding:'10px 16px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <Avatar c={c} size={36} />
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:'#111827' }}>{c.name}</div>
                        {c.is_blacklisted
                          ? <span style={{ fontSize:10, fontWeight:700, color:'#f87171' }}>⛔ Blacklisted</span>
                          : c.ranking && c.ranking!=='normal'
                            ? <span style={{ fontSize:10, fontWeight:600, color: c.ranking==='platinum'?'#1f2937':c.ranking==='gold'?'#ca8a04':'#3b82f6' }}>{c.ranking==='platinum'?'💎':c.ranking==='gold'?'🥇':'🥈'} {c.ranking}</span>
                            : c.is_new
                              ? <span style={{ fontSize:10, fontWeight:700, color:'#16a34a' }}>🟢 New</span>
                              : null}
                        {c.created_at && <div style={{ fontSize:10, color:'#9ca3af', marginTop:1 }}>Joined {new Date(c.created_at).toLocaleDateString('en-GB')}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding:'10px 16px', fontSize:13, color:'#374151' }} onClick={e=>e.stopPropagation()}>
                    <InlinePhoneEdit customer={c} onSaved={load} />
                  </td>
                  <td style={{ padding:'10px 16px' }} onClick={e=>e.stopPropagation()}>
                    <InlineLocationEdit customer={c} onSaved={load} />
                  </td>
                  <td style={{ padding:'10px 16px' }}>
                    {c.preferred_delivery
                      ? <span style={{ fontSize:11, background:'#e0f2fe', color:'#0369a1', padding:'2px 8px', borderRadius:10, fontWeight:600 }}>🚚 {DELIVERY_LABELS[c.preferred_delivery]||c.preferred_delivery}</span>
                      : <span style={{color:'#adb5bd',fontSize:12}}>—</span>}
                  </td>
                  <td style={{ padding:'10px 16px' }}>
                    <div style={{ fontSize:12, color:'#374151' }}>Bills: <span style={{fontWeight:700,color:'#111827'}}>{c.total_bills ?? 0}</span></div>
                    <div style={{ fontSize:12, color:'#374151' }}>Cancel: <span style={{fontWeight:700,color: c.cancelled_bills > 0 ? '#dc2626' : '#111827'}}>{c.cancelled_bills ?? 0}</span></div>
                    {c.last_order && <div style={{ fontSize:10, color:'#9ca3af', marginTop:2 }}>Last: {new Date(c.last_order).toLocaleDateString('en-GB')}</div>}
                  </td>
                  <td style={{ padding:'10px 16px' }}>
                    <div style={{ fontSize:12, color:'#374151' }}>Pending: <span style={{fontWeight:700,color: c.total_pending > 0 ? '#d97706' : '#6b7280'}}>${parseFloat(c.total_pending || 0).toFixed(2)}</span></div>
                    <div style={{ fontSize:12, color:'#374151' }}>Purchase: <span style={{fontWeight:700,color:'#16a34a'}}>${parseFloat(c.total_purchase || 0).toFixed(2)}</span></div>
                    <div style={{ fontSize:12, color:'#374151' }}>Cancelled: <span style={{fontWeight:700,color: c.total_cancelled > 0 ? '#dc2626' : '#6b7280'}}>${parseFloat(c.total_cancelled || 0).toFixed(2)}</span></div>
                  </td>
                </tr>
              ))}
              {customers.length===0 && <tr><td colSpan={4} style={{ padding:24, textAlign:'center', color:'#6c757d' }}>No customers found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Panel */}
      {selected && (
        <div style={{ background:'#fff', border:'1px solid #dee2e6', borderRadius:14, padding:20, height:'fit-content', position:'sticky', top:20 }}>
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}>
            <button onClick={()=>setSelected(null)} style={{ background:'none', border:'none', color:'#6c757d', cursor:'pointer', fontSize:18 }}>✕</button>
          </div>
          <div style={{ display:'flex', gap:12, alignItems:'center', marginBottom:14 }}>
            <Avatar c={selected} size={52} />
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <div style={{ fontWeight:700, fontSize:15, color:'#111827' }}>{selected.name}</div>
                <span title="Sync from Facebook" style={{ cursor:'pointer', fontSize:13, color:'#9ca3af' }} onClick={()=>toast('FB sync coming soon')}>🔄</span>
              </div>
              {selected.is_blacklisted
                ? <span style={{ fontSize:11, fontWeight:700, background:'#111827', color:'#fff', padding:'1px 7px', borderRadius:10 }}>⛔ Blacklisted</span>
                : selected.ranking && selected.ranking!=='normal'
                  ? <span style={{ fontSize:11, fontWeight:600, color: selected.ranking==='platinum'?'#7c3aed':selected.ranking==='gold'?'#ca8a04':'#3b82f6' }}>{selected.ranking==='platinum'?'💎':selected.ranking==='gold'?'🥇':'🥈'} {selected.ranking}</span>
                  : selected.is_new
                    ? <span style={{ fontSize:11, fontWeight:700, background:'#dcfce7', color:'#16a34a', padding:'1px 7px', borderRadius:10 }}>🟢 New</span>
                    : null}
            </div>
          </div>

          {/* Phones */}
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:11, fontWeight:600, color:'#9ca3af', marginBottom:4 }}>📞 PHONE</div>
            <InlinePhoneEdit customer={selected} onSaved={()=>{ load(); }} />
          </div>


          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:11, fontWeight:600, color:'#9ca3af', marginBottom:4 }}>📍 ADDRESS</div>
            <InlineLocationEdit customer={selected} onSaved={()=>{ load(); }} />
          </div>
          {selected.preferred_delivery && <div style={{ marginBottom:10 }}><span style={{ fontSize:12, background:'#e0f2fe', color:'#0369a1', padding:'2px 8px', borderRadius:10, fontWeight:600 }}>🚚 {DELIVERY_LABELS[selected.preferred_delivery]||selected.preferred_delivery}</span></div>}
          {selected.notes && <div style={{ fontSize:12, color:'#6b7280', background:'#f9fafb', borderRadius:6, padding:'6px 10px', marginBottom:10 }}>📝 {selected.notes}</div>}

          {/* Stats */}
          {selected.created_at && <div style={{ fontSize:11, color:'#9ca3af', marginBottom:8 }}>📅 Joined {new Date(selected.created_at).toLocaleString('en-GB', {day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'})}</div>}
          {stats && (
            <div style={{ background:'#f9fafb', borderRadius:8, padding:'10px 14px', marginBottom:10 }}>
              <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', marginBottom:8, textTransform:'uppercase', letterSpacing:'0.04em' }}>Customer Stats</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px 12px' }}>
                <div>
                  <div style={{ fontSize:11, color:'#9ca3af' }}>Total Bills</div>
                  <div style={{ fontSize:15, fontWeight:700, color:'#111827' }}>{stats.total_bills}</div>
                </div>
                <div>
                  <div style={{ fontSize:11, color:'#9ca3af' }}>Cancelled</div>
                  <div style={{ fontSize:15, fontWeight:700, color: stats.cancelled_bills > 0 ? '#dc2626' : '#111827' }}>{stats.cancelled_bills}</div>
                </div>
                <div>
                  <div style={{ fontSize:11, color:'#9ca3af' }}>Total Purchase</div>
                  <div style={{ fontSize:15, fontWeight:700, color:'#16a34a' }}>${parseFloat(stats.total_purchase).toFixed(2)}</div>
                </div>
                <div>
                  <div style={{ fontSize:11, color:'#9ca3af' }}>Total Cancelled</div>
                  <div style={{ fontSize:15, fontWeight:700, color: stats.total_cancelled > 0 ? '#dc2626' : '#6b7280' }}>${parseFloat(stats.total_cancelled).toFixed(2)}</div>
                </div>
              </div>
              {stats.last_order && <div style={{ marginTop:8, fontSize:11, color:'#6b7280' }}>🕐 Last order: {new Date(stats.last_order).toLocaleString('en-GB')}</div>}
            </div>
          )}

          {/* Ranking */}
          <div style={{ paddingTop:12, borderTop:'1px solid #dee2e6' }}>
            <label style={lbl}>Ranking</label>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {['normal','silver','gold','platinum'].map(r=>(
                <button key={r} onClick={async()=>{ try { await api.put(`/customers/${selected.id}/ranking`,{ranking:r}); toast.success(`Set to ${r}`); setSelected({...selected,ranking:r}); load(); } catch { toast.error('Failed'); } }}
                  style={{ background: selected.ranking===r?'#7c6af5':'#f3f4f6', color: selected.ranking===r?'#fff':'#374151', border:'none', borderRadius:6, padding:'5px 10px', fontSize:11, fontWeight:600, cursor:'pointer' }}>
                  {r==='platinum'?'💎':r==='gold'?'🥇':r==='silver'?'🥈':'⚪'} {r}
                </button>
              ))}
            </div>
          </div>

          {/* Preferred Delivery */}
          <div style={{ paddingTop:12, borderTop:'1px solid #dee2e6', marginTop:12 }}>
            <label style={lbl}>Preferred Delivery</label>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {deliveryMethods.map(m=>(
                <button key={m.code} onClick={async()=>{ const nv=selected.preferred_delivery===m.code?null:m.code; try { await updateCustomer(selected.id,{preferred_delivery:nv}); toast.success(nv?`Set to ${m.name}`:'Cleared'); setSelected({...selected,preferred_delivery:nv}); load(); } catch { toast.error('Failed'); } }}
                  style={{ background: selected.preferred_delivery===m.code?'#0369a1':'#e0f2fe', color: selected.preferred_delivery===m.code?'#fff':'#0369a1', border:'none', borderRadius:6, padding:'4px 10px', fontSize:11, fontWeight:600, cursor:'pointer' }}>{m.name}</button>
              ))}
            </div>
          </div>

          {/* Blacklist */}
          <div style={{ paddingTop:12, borderTop:'1px solid #dee2e6', marginTop:12 }}>
            <button onClick={async()=>{
              if (selected.is_blacklisted) { try { await api.put(`/customers/${selected.id}/ranking`,{is_blacklisted:0}); toast.success('Removed'); setSelected({...selected,is_blacklisted:0}); load(); } catch { toast.error('Failed'); } }
              else { const r=window.prompt('Blacklist reason:'); if (!r) return; try { await api.put(`/customers/${selected.id}/ranking`,{is_blacklisted:1,blacklist_reason:r}); toast.success('Blacklisted'); setSelected({...selected,is_blacklisted:1,blacklist_reason:r}); load(); } catch { toast.error('Failed'); } }
            }} style={{ background: selected.is_blacklisted?'#22c55e':'#111827', color:'#fff', border:'none', borderRadius:6, padding:'6px 12px', fontSize:12, fontWeight:600, cursor:'pointer', width:'100%' }}>
              {selected.is_blacklisted?'✅ Remove Blacklist':'⛔ Blacklist Customer'}
            </button>
            {selected.is_blacklisted && selected.blacklist_reason && <div style={{ fontSize:11, color:'#6b7280', marginTop:4 }}>Reason: {selected.blacklist_reason}</div>}
          </div>

          {/* Add Note */}
          <div style={{ marginTop:12, paddingTop:12, borderTop:'1px solid #dee2e6' }}>
            <label style={lbl}>Add Note</label>
            <textarea style={{ ...inp, minHeight:70, resize:'vertical', marginBottom:8 }} value={note} onChange={e=>setNote(e.target.value)} placeholder="Note about this customer..." />
            <button onClick={async()=>{ if(!note.trim())return; try{await addCustomerNote(selected.id,note);toast.success('Note added');setNote('');}catch{toast.error('Failed');} }} style={{ background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'8px 16px', fontSize:13, fontWeight:600, cursor:'pointer', width:'100%' }}>Add Note</button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editCustomer && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:300 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:28, width:460, maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ fontWeight:700, fontSize:17, marginBottom:20 }}>Edit — {editCustomer.name}</div>
            <div style={{ marginBottom:14 }}><label style={lbl}>Full Name *</label><input style={inp} value={editForm.name||''} onChange={e=>setEditForm(p=>({...p,name:e.target.value}))} required /></div>
            <div style={{ marginBottom:14 }}><label style={lbl}>Phone Numbers</label><PhoneEditor phones={editPhones} onChange={setEditPhones} /></div>
            <div style={{ marginBottom:14 }}><label style={lbl}>Address</label><textarea style={{ ...inp, minHeight:60, resize:'vertical' }} value={editForm.address||''} onChange={e=>setEditForm(p=>({...p,address:e.target.value}))} /></div>

            <div style={{ marginBottom:14 }}><label style={lbl}>Preferred Delivery</label>
              <select style={inp} value={editForm.preferred_delivery||''} onChange={e=>setEditForm(p=>({...p,preferred_delivery:e.target.value}))}>
                <option value="">-- Not Set --</option>{deliveryMethods.map(m=><option key={m.code} value={m.code}>{m.name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom:20 }}><label style={lbl}>Notes</label><textarea style={{ ...inp, minHeight:60, resize:'vertical' }} value={editForm.notes||''} onChange={e=>setEditForm(p=>({...p,notes:e.target.value}))} /></div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={async()=>{
                const phones = editPhones.map(cleanPhone).filter(Boolean);
                try { await updateCustomer(editCustomer.id, { ...editForm, phones, phone: phones[0]||'', phone2: phones[1]||'' }); toast.success('Updated'); setEditCustomer(null); if(selected?.id===editCustomer.id) setSelected({...selected,...editForm,phones}); load(); }
                catch { toast.error('Failed'); }
              }} style={{ background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:10, flex:1, fontSize:14, fontWeight:600, cursor:'pointer' }}>Save</button>
              <button onClick={()=>setEditCustomer(null)} style={{ background:'#dee2e6', color:'#212529', border:'none', borderRadius:8, padding:10, flex:1, fontSize:14, cursor:'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* New Customer Modal */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200 }}>
          <div style={{ background:'#fff', border:'1px solid #dee2e6', borderRadius:16, padding:28, width:420, maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ fontWeight:700, fontSize:17, marginBottom:20, color:'#111827' }}>New Customer</div>
            <form onSubmit={handleCreate}>
              <div style={{ marginBottom:14 }}><label style={lbl}>Full Name *</label><input name="name" style={inp} required /></div>
              <div style={{ marginBottom:14 }}><label style={lbl}>Phone Numbers</label><PhoneEditor phones={newPhones} onChange={setNewPhones} /></div>
              <div style={{ marginBottom:14 }}><label style={lbl}>Address</label><textarea name="address" style={{ ...inp, minHeight:60, resize:'vertical' }} /></div>

              <div style={{ marginBottom:20 }}><label style={lbl}>Preferred Delivery</label>
                <select name="preferred_delivery" style={inp} defaultValue="">
                  <option value="">-- Not Set --</option>{deliveryMethods.map(m=><option key={m.code} value={m.code}>{m.name}</option>)}
                </select>
              </div>
              <div style={{ marginBottom:20 }}><label style={lbl}>Ranking</label>
                <select name="ranking" style={inp} defaultValue="normal">
                  <option value="normal">⚪ Normal</option>
                  <option value="silver">🥈 Silver</option>
                  <option value="gold">🥇 Gold</option>
                  <option value="platinum">💎 Platinum</option>
                </select>
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button type="submit" style={{ background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:10, flex:1, fontSize:14, fontWeight:600, cursor:'pointer' }}>Save</button>
                <button type="button" onClick={()=>{setShowForm(false);setNewPhones(['']);}} style={{ background:'#dee2e6', color:'#212529', border:'none', borderRadius:8, padding:10, flex:1, fontSize:14, cursor:'pointer' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRankings && <RankingsModal onClose={()=>setShowRankings(false)} />}
      {showDelivery && <DeliveryMethodsModal onClose={()=>setShowDelivery(false)} />}
    </div>
  );
}
